import { Check, CheckCheck, Clock, AlertCircle } from 'lucide-react';

/**
 * MessageStatus — sender-side status indicator for an outgoing message.
 *
 *   sending    → clock icon
 *   failed     → red alert icon
 *   sent       → single check (grey)
 *   delivered  → double check (grey)
 *   read       → double check (#002FA7 brand blue) — only when read
 *                receipts are enabled
 *
 * The status is derived from the message doc:
 *   • _pending         → "sending"
 *   • _failed          → "failed"
 *   • read_by[]        → "read"
 *   • delivered_to[]   → "delivered"
 *   • status/read_at/delivered_at fallbacks for older backend payloads
 *   • otherwise        → "sent"
 *
 * `selfId` is the current user's id; `recipientIds` is every OTHER
 * participant.
 *
 * Rules:
 *   • 1:1 chat: the sole recipient flips the state as soon as they are
 *     included in delivered_to[] / read_by[].
 *   • Group chat: ALL recipients must have crossed the threshold before
 *     the sender sees the upgraded state, so we never overstate delivery
 *     or read status.
 */
export default function MessageStatus({
  message,
  selfId,
  recipientIds = [],
  readReceiptsEnabled = true,
  size = 'sm',
}) {
  if (!message) return null;

  const iconClass = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  if (message._failed) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-rose-500"
        title="Failed to send"
        data-testid="msg-status-failed"
      >
        <AlertCircle className={iconClass} />
      </span>
    );
  }

  if (message._pending) {
    return (
      <span
        className="inline-flex items-center gap-0.5 opacity-60"
        title="Sending…"
        data-testid="msg-status-sending"
      >
        <Clock className={iconClass} />
      </span>
    );
  }

  const normaliseId = (value) => {
    if (value === null || value === undefined) return null;
    return String(value);
  };

  const self = normaliseId(selfId);

  const ids = Array.from(
    new Set(
      (recipientIds || [])
        .map(normaliseId)
        .filter(Boolean)
        .filter((id) => id !== self),
    ),
  );

  const readBy = new Set(
    (message.read_by || [])
      .map(normaliseId)
      .filter(Boolean)
      .filter((id) => id !== self),
  );

  const deliveredTo = new Set(
    (message.delivered_to || [])
      .map(normaliseId)
      .filter(Boolean)
      .filter((id) => id !== self),
  );

  const isOneToOne = ids.length <= 1;

  const hasAnyRead = ids.length > 0 && ids.some((id) => readBy.has(id));
  const allRead = ids.length > 0 && ids.every((id) => readBy.has(id));

  // A read receipt also proves delivery, so read_by counts towards
  // delivered for the grey two-tick state when read receipts are hidden
  // or before the coloured read state is shown.
  const hasAnyDelivered = ids.length > 0 && ids.some((id) => deliveredTo.has(id) || readBy.has(id));
  const allDelivered = ids.length > 0 && ids.every((id) => deliveredTo.has(id) || readBy.has(id));

  const legacyStatus = String(message.status || '').toLowerCase();
  const legacyRead = legacyStatus === 'read' || Boolean(message.read_at);
  const legacyDelivered = legacyStatus === 'delivered' || legacyRead || Boolean(message.delivered_at);

  const isRead = readReceiptsEnabled && (
    isOneToOne
      ? (hasAnyRead || legacyRead)
      : allRead
  );

  const isDelivered = isOneToOne
    ? (hasAnyDelivered || legacyDelivered)
    : allDelivered;

  if (isRead) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[#002FA7] dark:text-[#7985FD]"
        title="Read"
        data-testid="msg-status-read"
      >
        <CheckCheck className={iconClass} />
      </span>
    );
  }

  if (isDelivered) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-theme-muted"
        title="Delivered"
        data-testid="msg-status-delivered"
      >
        <CheckCheck className={iconClass} />
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-0.5 text-theme-muted/70"
      title="Sent"
      data-testid="msg-status-sent"
    >
      <Check className={iconClass} />
    </span>
  );
}

/**
 * formatBubbleTimestamp — renders a friendly timestamp for every message
 * bubble, matching the brief:
 *   • Today        → "14:35"
 *   • Yesterday    → "Yesterday 14:35"
 *   • This year    → "24 Apr, 14:35"
 *   • Older        → "24 Apr 2024, 14:35"
 */
export function formatBubbleTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();

  if (sameDay) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }

  const sameYear = d.getFullYear() === now.getFullYear();

  if (sameYear) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ', ' +
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }

  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
